import { useCallback, useEffect, useRef, useState } from 'react';

const ELAPSED_TICK_MS = 100;
const WAVEFORM_SAMPLE_COUNT = 32;
const WAVEFORM_TICK_MS = 50;

export type RecorderStatus =
  | 'idle'
  | 'permission-pending'
  | 'permission-denied'
  | 'recording'
  | 'paused'
  | 'review'
  | 'cancelled';

export type Take = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
};

export type UseRecorderResult = {
  status: RecorderStatus;
  elapsedMs: number;
  waveformAmplitudes: number[];
  take: Take | null;
  start: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  cancel: () => void;
  reset: () => void;
};

export function useRecorder(): UseRecorderResult {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const [waveformAmplitudes, setWaveformAmplitudes] = useState<number[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const [take, setTake] = useState<Take | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const elapsedAtStopRef = useRef<number>(0);

  useEffect(() => {
    if (status !== 'recording') return;
    const intervalId = setInterval(() => {
      setElapsedMs((prev) => prev + ELAPSED_TICK_MS);
    }, ELAPSED_TICK_MS);
    return () => clearInterval(intervalId);
  }, [status]);

  useEffect(() => {
    if (status !== 'recording' || !analyserRef.current) return;
    const buffer = new Uint8Array(WAVEFORM_SAMPLE_COUNT);
    const sample = (): void => {
      analyserRef.current!.getByteTimeDomainData(buffer);
      const normalised = Array.from(buffer, (byte) => Math.abs(byte - 128) / 128);
      setWaveformAmplitudes(normalised);
    };
    sample();
    const intervalId = setInterval(sample, WAVEFORM_TICK_MS);
    return () => clearInterval(intervalId);
  }, [status]);

  const releaseStream = useCallback((): void => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    analyserRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;
  }, []);

  const start = useCallback(async (): Promise<void> => {
    setStatus('permission-pending');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.start();
      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);
        analyserRef.current = analyser;
      } catch {
        analyserRef.current = null;
      }
      setElapsedMs(0);
      setWaveformAmplitudes([]);
      setTake(null);
      setStatus('recording');
    } catch {
      setStatus('permission-denied');
    }
  }, []);

  const pause = useCallback((): void => {
    recorderRef.current?.pause();
    setStatus('paused');
  }, []);

  const resume = useCallback((): void => {
    recorderRef.current?.resume();
    setStatus('recording');
  }, []);

  const stop = useCallback((): void => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    elapsedAtStopRef.current = elapsedMs;
    recorder.ondataavailable = (event: BlobEvent): void => {
      setTake({
        blob: event.data,
        mimeType: recorder.mimeType,
        durationMs: elapsedAtStopRef.current,
      });
    };
    recorder.stop();
    releaseStream();
    setStatus('review');
  }, [elapsedMs, releaseStream]);

  const cancel = useCallback((): void => {
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      if (recorder.state !== 'inactive') recorder.stop();
    }
    releaseStream();
    setTake(null);
    setStatus('cancelled');
  }, [releaseStream]);

  const reset = useCallback((): void => {
    setTake(null);
    setStatus('idle');
    setElapsedMs(0);
  }, []);

  return {
    status,
    elapsedMs,
    waveformAmplitudes,
    take,
    start,
    pause,
    resume,
    stop,
    cancel,
    reset,
  };
}
